import React, {HTMLAttributes} from 'react'
import styled from '@emotion/styled'
import {ThemeType} from 'types/Theme'

import {cssTheme} from '../../../../themes'
import {linariaMq} from '../../../../utils/breakpoints'

type Spacing = keyof ThemeType['layout']['spacing']

type overflowTypes = 'auto' | 'scroll' | 'hidden'

interface Props {
  /** Content to be displayed inside the Stack */
  children: React.ReactNode
  /** Horizontal space between elements. **/
  columnGap?: Spacing
  /** Vertical space between elements when they wrap on desktop. **/
  rowGap?: Spacing
  /** Accepts a custom class name, also adds the ability to customize the component using styled(HorizontalScroll), use this to add page margins */
  className?: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Scroll event */
  onScroll?: HTMLAttributes<HTMLDivElement>['onScroll']
  /** Horizontal overflow */
  overflowX?: overflowTypes
}

const HorizontalScrollContainer = styled.div<{
  columnGap: Props['columnGap']
  rowGap: Props['rowGap']
  overflowX: overflowTypes
}>`
  display: flex;
  align-items: center;
  overflow-x: ${({overflowX}) => overflowX};
  column-gap: ${({columnGap}) =>
    columnGap ? cssTheme.layout.spacing[columnGap] : 'inherit'};

  /* hide scrollbar */
  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */
  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari and Opera */
  }

  & > * {
    flex-shrink: 0;
  }

  ${linariaMq.desktopXs} {
    flex-wrap: wrap;
    row-gap: ${({rowGap}) =>
      rowGap ? cssTheme.layout.spacing[rowGap] : 'inherit'};
  }
`

export const HorizontalScroll = ({
  className,
  dataId,
  children,
  columnGap = 's000',
  rowGap = 's000',
  overflowX = 'scroll',
  onScroll
}: Props) => (
  <HorizontalScrollContainer
    className={className}
    data-id={dataId}
    columnGap={columnGap}
    rowGap={rowGap}
    overflowX={overflowX}
    {...(onScroll ? {onScroll} : {})}
  >
    {children}
  </HorizontalScrollContainer>
)
